import { useI18n } from 'vue-i18n';
import { computed, ref, watch, onMounted, unref } from 'vue';
import { get } from 'lodash';
import type { Ref } from 'vue';

import { NovaDate, YardTaskStatus, namesToFullName, YardTaskEvent } from '@nova/core';
import { transformApiResponseDataToEditData } from './EventTriggeredEditTaskDrawer.helpers';
import { useNotify } from '@/composables';
import useYard from '@/modules/yard/composables/useYard';
import yardTasksService from '@satellite/services/yard-tasks-service';

type User = {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
};

// TODO: consolidate types with neutron and circe
type TaskEvent = {
  id: string;
  createDateTime: string;
  event: YardTaskEvent;
  reason: string | null;
  data: {
    user: User;
  };
  yardTaskId: string;
};

type Task = {
  id?: string;
  refNum?: number;
  description?: string;
  assignedTo?: User | null;
  assignedBy?: User;
  createdBy?: User;
  dueDateTime?: string | null;
  createDateTime?: string;
  status?: string;
  type?: string;
  tags?: string[];
  events?: TaskEvent[];
  child?: {
    category?: string;
    relatedEntity?: string;
    relatedId?: string;
    relatedObject?: any;
    checklist?: any[];
    assetContainer?: any;
    originSpot?: any;
    destinationSpot?: any;
  };
};

export interface UseYardTaskPresenterOptions {
  taskId?: Ref<string | null | undefined>;
  task?: Ref<Task | null | undefined>;
}

export function useYardTaskPresenter(
  { taskId, task }: UseYardTaskPresenterOptions = {},
  lazy = false
) {
  const yardStore = useYard();
  const { t } = useI18n();
  const notify = useNotify();

  const fetchedTask = ref(null);
  const isLoading = ref(false);

  const currentTask = computed(() => {
    return { ...task?.value, ...fetchedTask.value };
  });

  const transformedTask = computed(() => {
    if (!currentTask.value || !warehouseTimezone.value) {
      return null;
    }

    return transformApiResponseDataToEditData(currentTask.value, warehouseTimezone.value);
  });

  const warehouseTimezone = computed(() => {
    return yardStore.selectedWarehouseRef?.value?.timezone;
  });

  const formatDate = (date: string | null, format: string = 'MMM d, yyyy @ h:mm a') => {
    if (!date) {
      return null;
    }

    try {
      return new NovaDate(date).format(format, { tz: warehouseTimezone.value });
    } catch (error) {
      console.error('Error formatting due date:', error);
      return null;
    }
  };

  const formatPersonName = (
    person?: { firstName?: string | null; lastName?: string | null } | null
  ) => {
    if (!person) {
      return null;
    }

    const { firstName, lastName } = person;
    return namesToFullName(firstName, lastName);
  };

  const formatTaskNumber = (taskNumber?: string | null) => {
    if (!taskNumber) {
      return null;
    }

    return String(taskNumber).padStart(3, '0');
  };

  const formatDueDate = (dueDate?: string | null) => {
    const formattedDueDate = formatDate(dueDate);

    return formattedDueDate || t('yard.pages.yardTasks.messages.noDueDate');
  };

  const formatCreatedAt = (createdAt?: string | null) => {
    const formattedCreatedAt = formatDate(createdAt);

    return formattedCreatedAt || '-';
  };

  const formatAssignedToName = (
    assignee?: { firstName?: string | null; lastName?: string | null } | null
  ) => {
    return formatPersonName(assignee) || t('yard.pages.yardTasks.messages.unassigned');
  };

  const getStatusIcon = (status?: string | null) => {
    if (status === YardTaskStatus.IN_PROGRESS) {
      return 'mdi-progress-clock';
    }

    if (status === YardTaskStatus.COMPLETED) {
      return 'mdi-check-circle';
    }

    if (status === YardTaskStatus.CANCELLED) {
      return 'mdi-close-circle';
    }

    return 'mdi-progress-helper';
  };

  const getStatusLabel = (status?: string | null) => {
    if (status === YardTaskStatus.IN_PROGRESS) {
      return t('yard.pages.yardTasks.labels.inProgress');
    }

    if (status === YardTaskStatus.COMPLETED) {
      return t('yard.pages.yardTasks.labels.completed');
    }

    if (status === YardTaskStatus.CANCELLED) {
      return t('yard.pages.yardTasks.labels.canceled');
    }

    return t('yard.pages.yardTasks.labels.toDo');
  };

  const getEvents = () => {
    const rawEvents = get(currentTask.value, 'events');

    if (Array.isArray(rawEvents)) {
      return rawEvents;
    }

    return [];
  };

  const getUserFromEvent = (event: TaskEvent | null) => {
    if (!event) {
      return '---';
    }

    const user = get(event, 'data.user');
    if (user) {
      return formatPersonName(user);
    }

    return '---';
  };

  const getTransitionEventByEvent = (eventType: YardTaskEvent) => {
    const events = getEvents();

    const event =
      events.find((event: TaskEvent) => {
        return event.event === eventType;
      }) || null;

    if (!event) {
      return null;
    }

    return {
      at: event.createDateTime ? formatDate(event.createDateTime) : null,
      rawAt: event.createDateTime || null,
      by: getUserFromEvent(event),
      rawBy: get(event, 'data.user') || null
    };
  };

  const taskNumber = computed(() => {
    return formatTaskNumber(currentTask.value?.refNum);
  });

  const taskDescription = computed(() => {
    return currentTask.value?.description || null;
  });

  const taskStatus = computed(() => {
    return currentTask.value?.status || null;
  });

  const assignedToName = computed(() => {
    return formatAssignedToName(currentTask.value?.assignedTo);
  });

  const dueDate = computed(() => {
    return formatDueDate(currentTask.value?.dueDateTime);
  });

  const statusIcon = computed(() => {
    return getStatusIcon(taskStatus.value);
  });

  const statusLabel = computed(() => {
    return getStatusLabel(taskStatus.value);
  });

  const hasUrgentTag = computed(() => {
    if (!currentTask.value?.tags || !Array.isArray(currentTask.value.tags)) {
      return false;
    }

    return currentTask.value.tags.includes('urgent');
  });

  const taskCategory = computed(() => {
    return currentTask.value?.child?.category || null;
  });

  const relatedEntity = computed(() => {
    return currentTask.value?.child?.relatedEntity || null;
  });

  const relatedId = computed(() => {
    return currentTask.value?.child?.relatedId || null;
  });

  const relatedObject = computed(() => {
    return currentTask.value?.child?.relatedObject || null;
  });

  const createdBy = computed(() => {
    return formatPersonName(currentTask.value?.createdBy);
  });

  const createdAt = computed(() => {
    return formatDate(currentTask.value?.createDateTime);
  });

  const inProgressEvent = computed(() => {
    return getTransitionEventByEvent(YardTaskEvent.IN_PROGRESS);
  });

  const completedEvent = computed(() => {
    return getTransitionEventByEvent(YardTaskEvent.COMPLETED);
  });

  const cancelledEvent = computed(() => {
    return getTransitionEventByEvent(YardTaskEvent.CANCELLED);
  });

  const inProgressBy = computed(() => inProgressEvent.value?.by || null);
  const completedBy = computed(() => completedEvent.value?.by || null);
  const cancelledBy = computed(() => cancelledEvent.value?.by || null);

  const inProgressAt = computed(() => inProgressEvent.value?.at || null);
  const completedAt = computed(() => completedEvent.value?.at || null);
  const cancelledAt = computed(() => cancelledEvent.value?.at || null);

  const isTaskCreated = computed(() => {
    return taskStatus.value === YardTaskStatus.CREATED;
  });

  const isTaskInProgress = computed(() => {
    return taskStatus.value === YardTaskStatus.IN_PROGRESS;
  });

  const isTaskCompleted = computed(() => {
    return taskStatus.value === YardTaskStatus.COMPLETED;
  });

  const isTaskCancelled = computed(() => {
    return taskStatus.value === YardTaskStatus.CANCELLED;
  });

  const fetchTask = async (id: string) => {
    if (!id) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await yardTasksService.getYardTask(id);
      fetchedTask.value = response;
    } catch (error) {
      notify(t('yard.messages.failedToFetchYardTask'), 'error');
      fetchedTask.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = () => {
    fetchedTask.value = null;

    const currentTaskIdValue = unref(taskId);
    const currentTaskValue = unref(task);
    const id = currentTaskIdValue || currentTaskValue?.id;

    if (id) {
      fetchTask(id);
    }
  };

  watch([() => unref(taskId), () => unref(task)?.id], () => refresh());

  onMounted(() => {
    if (!lazy && taskId) {
      refresh();
    }
  });

  return {
    isLoading,
    currentTask,
    transformedTask,

    taskNumber,
    taskDescription,
    taskStatus,
    assignedToName,
    dueDate,
    statusIcon,
    statusLabel,
    hasUrgentTag,
    taskCategory,
    relatedEntity,
    relatedId,
    relatedObject,
    warehouseTimezone,

    inProgressEvent,
    completedEvent,
    cancelledEvent,
    createdBy,
    inProgressBy,
    completedBy,
    cancelledBy,
    createdAt,
    inProgressAt,
    completedAt,
    cancelledAt,

    isTaskCreated,
    isTaskInProgress,
    isTaskCompleted,
    isTaskCancelled,

    formatPersonName,
    formatDueDate,
    formatCreatedAt,
    formatTaskNumber,
    getStatusIcon,
    getStatusLabel,

    fetchTask,
    refresh
  };
}
